import React, {useRef} from "react";
import FarmersSampleController from "../../controllers/FarmersSampleController";
import {RenderTableStruct, SubmissionPrepareBase} from "../submission/SubmissionPrepareBase";
import PrepareSubmissionFarmer from "../../controllers/PrepareSubmissionFarmer";
import SubmissionTable, {SubmissionPrepareFunctions} from "./SubmissionTable";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import {arrayUpdate} from "../../immutableState";
import Lab from "../../controllers/Lab";
import TestType from "../../controllers/TestType";
import {insertAt, split} from "../../array";


const csvPopulateFarmer = {
    "Sample Number": (value: string, row: PrepareSubmissionFarmer) => {
        row.sampleNumber = value;
    },
    "Block": (value: string, row: PrepareSubmissionFarmer) => {
        row.blockNumber = value;
    },
    "Test": (value: string, row: PrepareSubmissionFarmer, data: SubmissionDataResponse<FarmerRow>) => {
        const testsGiven = value.toLowerCase().trim();
        const test = data.tests.find(t => testsGiven == t.value.toLowerCase().trim())?.key;
        if (!test) return;
        row.test = test;
    },
};

export type FarmerRow = PrepareSubmissionFarmer & { selected?: boolean };

export function emptyFarmerRow(): FarmerRow {
    return {
        testId: 0,
        testDataId: null,
        submissionId: null,
        blockNumber: "",
        sampleNumber: "",
        notes: "",
        delete: false,
        test: TestType.ItestFarmerSample,
        agronomist: "",
        farmName: "",
        depot: "",
        clientName: "",
        agent: "",
        appAdded: new Date(),
        rawLab: "",
        optionalTests: [],
    };
}

function allSelectedOrUnselected(farmerRow: FarmerRow[]){
    return farmerRow.filter(s => s.selected).length > 0 && farmerRow.length !== farmerRow.filter(s => s.selected).length
}

export default function SubmissionPrepareFarmer(props: { dataIds: number[]; close: (saved: boolean) => void }) {
    const submitRef = useRef<SubmissionPrepareFunctions>();
    function renderTable(submissionData: SubmissionDataResponse<FarmerRow>, data: FarmerRow[][], setData: (newData :FarmerRow[][]) => void, setShowUpload : (show: {show: boolean, index: number}) => void): RenderTableStruct {
        function addRow(index: number) {
            const newArray = data.map((c, i) => index !== i ? c : c.concat(emptyFarmerRow()))
            setData(newArray)
        }

        function openCsvButton(index: number) {
            setShowUpload({show: true, index})
        }
        
        let result = 
        <>
            {data.map((rowArr, index) => (
                <div key={index}>
                    <SubmissionTable index={index} data={submissionData} leafs={rowArr} submitRef={submitRef}
                                     setData={d => {
                                         setData(arrayUpdate(data, index, d))
                                     }}/>

                    <div className='flex justify-between p-1 mb-1 pb-4 border-b border-gray-400'>
                        <div className='flex items-center'>
                            <div className='btn bg-primary-500' onClick={() => addRow(index)}>+</div>
                            <div className='btn bg-primary-500' onClick={() => openCsvButton(index)}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                     strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
                                    <path strokeLinecap="round" strokeLinejoin="round"
                                          d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"/>
                                </svg>
                            </div>
                        </div>

                        {allSelectedOrUnselected(rowArr)
                            ? <div className='btn bg-primary' onClick={() => {
                                const part = split(rowArr, d => d.selected ?? false);
                                const mutate = data.map(d => d);
                                mutate.splice(index, 1);
                                insertAt(mutate, index, part.one.map(d => ({...d, selected: false})), part.two);
                                setData(mutate);
                            }}>Split</div>
                            : null
                        }
                    </div>
                </div>
            ))}
        </>;

        return {
            render: result,
            validation: () => submitRef.current?.validate() || false 
        }
    }

    return (
        <SubmissionPrepareBase
            labs={[Lab.ManualFarmer, Lab.NvirotekFarmer, Lab.WaypointFarmer]}
            testDataIds={props.dataIds}
            close={props.close}
            prepare={FarmersSampleController.prepareSubmission}
            submit={FarmersSampleController.submit}
            renderTable={renderTable}
            csvPopulateMap={csvPopulateFarmer}
            emptyRow={emptyFarmerRow}
        />
    );
}
